import { useState, useEffect, useMemo, useRef } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { supabase } from '@/lib/supabaseClient';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { useProducts } from '@/contexts/ProductsContext';
import {
  placeCoreOrder,
  initGatewayPayment,
  saveReceiptCopy,
  savePendingCheckoutSnapshot,
  slugToSku,
  type PlaceOrderErrorCode,
  type InitGatewayPaymentFailure,
} from '@/lib/coreOrder';
import { saveShippingAddress } from '@/lib/myAccount';
import { trackInitiateCheckout, trackPurchase } from '@/lib/tracking';
import { previewAutoDiscount, validateCoupon, type CartItemForDiscount, type DiscountPreview } from '@/lib/discountApi';
import { BD_DISTRICTS_BY_DIVISION } from '@/constants/bdDistricts';

const ALL_DISTRICT_NAMES = BD_DISTRICTS_BY_DIVISION.flatMap((g) => g.districts.map((d) => d.name)).sort((a, b) => a.localeCompare(b));
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';

interface CheckoutForm {
  fullName: string;
  phone: string;
  email: string;
  address1: string;
  address2: string;
  city: 'Dhaka' | 'Outside Dhaka';
  district: string;
  area: string;
  postalCode: string;
  deliveryNote: string;
  paymentMethod: string;
}

interface PaymentGatewayOption {
  gateway_key: string;
  display_name: string;
  is_enabled: boolean;
  display_order: number;
}

// A searchable dropdown for long option lists (64 districts, up to ~55
// thanas) — a native <select>'s open panel isn't reliably height-
// constrained across browsers/devices and can't be searched by typing.
// This renders its own fixed max-height, internally-scrolling panel
// instead, with a search box to filter (no more scrolling past 60 options
// to find one).
//
// Mobile-specific care taken here (a naive port of a desktop dropdown
// breaks easily on touch):
// - Closes on any outside mousedown/touchstart via a container ref,
//   instead of the input's onBlur — blur-based closing races against a
//   tap's synthetic click on touch devices and can eat the selection.
// - Search input is text-base (16px): anything smaller makes iOS Safari
//   auto-zoom the page when the field is focused.
// - Option rows use py-2.5 (bigger touch target) on mobile, tightening to
//   py-1.5 from md: up, where a mouse is precise enough not to need it.
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const filtered = query.trim() ? options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase())) : options;

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3.5 py-3 md:py-2.5 rounded-xl border border-[#E8E8E6] text-base md:text-sm text-left focus:outline-none focus:border-[#7B1113] transition-colors bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
      >
        <span className={value ? 'text-[#2A2A2A] truncate' : 'text-[#8A8A8A] truncate'}>{value || placeholder}</span>
        <i className={`ri-arrow-down-s-line text-base text-[#8A8A8A] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white rounded-xl border border-[#E8E8E6] shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#E8E8E6]">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-base md:text-sm rounded-lg border border-[#E8E8E6] text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113]"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-[#8A8A8A]">No matches</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-4 py-2.5 md:py-1.5 text-sm cursor-pointer transition-colors ${o === value ? 'bg-[#7B1113]/5 text-[#7B1113] font-medium' : 'text-[#2A2A2A] hover:bg-[#F0EFED]'}`}
                >
                  {o}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function parsePrice(p: string) {
  return Number(p.replace(/[^0-9]/g, ''));
}

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, totalPrice, clearCart } = useCart();
  const { products, routineSet } = useProducts();

  const getProductInfo = (slug: string) => {
    if (slug === '3-step-set') {
      return routineSet ? { name: routineSet.name, price: routineSet.price } : null;
    }
    const p = products.find((x) => x.id === slug);
    return p ? { name: p.name, price: p.price } : null;
  };

  // Fires once on entering checkout with whatever's in the cart — not
  // re-fired on later qty edits on this same page. Waits for products/
  // routineSet to actually be loaded first: on first mount `products` is
  // still empty (async fetch in ProductsContext), so getProductInfo would
  // resolve every line to a fallback name and ৳0 if this fired immediately.
  const hasTrackedCheckout = useRef(false);
  useEffect(() => {
    if (hasTrackedCheckout.current) return;
    if (cart.length === 0) return;
    if (products.length === 0 && !routineSet) return;
    hasTrackedCheckout.current = true;
    const items = cart.map((item) => {
      const info = getProductInfo(item.slug);
      return { id: item.slug, name: info?.name ?? item.slug, price: info ? parsePrice(info.price) : 0, quantity: item.qty };
    });
    trackInitiateCheckout(items, totalPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, products, routineSet, totalPrice]);

  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { isAuthenticated, currentUser, session } = useAuth();

  const [form, setForm] = useState<CheckoutForm>({
    fullName: '',
    phone: '',
    email: '',
    address1: '',
    address2: '',
    city: 'Dhaka',
    district: '',
    area: '',
    postalCode: '',
    deliveryNote: '',
    paymentMethod: 'cod',
  });

  const [paymentGateways, setPaymentGateways] = useState<PaymentGatewayOption[]>([]);

  useEffect(() => {
    supabase
      .from('core_payment_gateways_public_v')
      .select('gateway_key, display_name, is_enabled, display_order')
      .then(({ data }) => setPaymentGateways((data as PaymentGatewayOption[]) ?? []));
  }, []);

  // Admin-configurable in ERP > Delivery Merchants (defaults match what the
  // server falls back to if this lookup ever fails, so pricing never breaks).
  const [shippingRates, setShippingRates] = useState({ dhaka: 60, outside: 120, freeThreshold: 3000 });
  useEffect(() => {
    supabase
      .from('core_shipping_rates_public_v')
      .select('dhaka_charge_bdt, outside_dhaka_charge_bdt, free_shipping_threshold_bdt')
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setShippingRates({
            dhaka: Number(data.dhaka_charge_bdt),
            outside: Number(data.outside_dhaka_charge_bdt),
            freeThreshold: Number(data.free_shipping_threshold_bdt),
          });
        }
      });
  }, []);

  // Admin-configurable in ERP > Inventory > Products (SKU "GIFT-BOX") — free
  // automatically when the cart includes the 3-step combo (server-verified
  // in core_create_order, never trusted from here), otherwise this is what
  // it costs to add.
  const [giftBoxPrice, setGiftBoxPrice] = useState<number | null>(null);
  useEffect(() => {
    supabase
      .from('core_addon_products_public_v')
      .select('sku, msrp_bdt')
      .eq('sku', 'GIFT-BOX')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.msrp_bdt != null) setGiftBoxPrice(Number(data.msrp_bdt));
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      setForm((prev) => ({
        ...prev,
        fullName: currentUser.name,
        phone: currentUser.phone.replace('+880 ', ''),
        email: currentUser.email,
        ...(currentUser.savedAddress && {
          address1: currentUser.savedAddress.address1,
          address2: currentUser.savedAddress.address2,
          city: currentUser.savedAddress.city as 'Dhaka' | 'Outside Dhaka',
          area: currentUser.savedAddress.area,
          postalCode: currentUser.savedAddress.postalCode,
        }),
      }));
    }
  }, [isAuthenticated, currentUser]);

  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<PlaceOrderErrorCode | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [noteBoxOpen, setNoteBoxOpen] = useState(false);

  const discountItems = useMemo<CartItemForDiscount[]>(() => {
    return cart.flatMap((item) => {
      const sku = slugToSku(item.slug);
      const p = getProductInfo(item.slug);
      return sku && p ? [{ sku, qty: item.qty, unitPrice: parsePrice(p.price) }] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getProductInfo closes over products/routineSet, both already listed
  }, [cart, products, routineSet]);

  const [autoDiscount, setAutoDiscount] = useState<DiscountPreview | null>(null);
  const [couponBoxOpen, setCouponBoxOpen] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponDiscount, setCouponDiscount] = useState<DiscountPreview | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);

  useEffect(() => {
    if (discountItems.length === 0) {
      setAutoDiscount(null);
      return;
    }
    previewAutoDiscount(discountItems).then(setAutoDiscount);
  }, [discountItems]);

  const effectiveDiscount = couponDiscount ?? autoDiscount;
  const discountAmount = effectiveDiscount?.amount ?? 0;

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCheckingCoupon(true);
    setCouponError(null);
    const result = await validateCoupon(couponInput, discountItems);
    if ('error' in result) {
      setCouponDiscount(null);
      setCouponError(result.error);
    } else {
      setCouponDiscount(result.preview);
    }
    setCheckingCoupon(false);
  };

  const handleRemoveCoupon = () => {
    setCouponDiscount(null);
    setCouponInput('');
    setCouponError(null);
    setCouponBoxOpen(false);
  };

  // .trim() 필수 — 공백 문자만으로는 값이 채워진 것으로 취급하지 않는다
  // (예: 이름란에 스페이스바만 입력해도 버튼이 활성화되던 문제 수정).
  const isRequiredFieldsFilled =
    form.fullName.trim() !== '' &&
    form.phone.trim() !== '' &&
    form.address1.trim() !== '' &&
    form.district.trim() !== '' &&
    form.area.trim() !== '';

  const selectedDistrictUpazilas = useMemo(
    () => BD_DISTRICTS_BY_DIVISION.flatMap((g) => g.districts).find((d) => d.name === form.district)?.upazilas ?? [],
    [form.district],
  );

  const shippingCost = form.city === 'Dhaka' ? shippingRates.dhaka : shippingRates.outside;
  const freeShipping = totalPrice >= shippingRates.freeThreshold;
  const finalShipping = freeShipping ? 0 : shippingCost;

  // The 3-step-set's cart price is already the bundle's own (discounted)
  // price — totalPrice/total below are correct as-is for what's actually
  // charged. This is purely a *display* breakdown so Subtotal shows the
  // true ৳8,500 component value with the ৳500 combo saving called out
  // explicitly, instead of a single opaque ৳8,000 line (matches how the
  // invoice and Products page already show it).
  const comboDiscount = cart.reduce((sum, item) => {
    if (item.slug !== '3-step-set' || !routineSet) return sum;
    return sum + parsePrice(routineSet.savings) * item.qty;
  }, 0);
  const displaySubtotal = totalPrice + comboDiscount;

  // The routine set, or any other set flagged gift_box_free in ERP.
  const isGiftBoxSet = (slug: string) => slug === '3-step-set' || !!products.find((p) => p.id === slug)?.giftBoxFree;
  const hasCombo = cart.some((item) => isGiftBoxSet(item.slug));
  // One combo set = one box (mirrors NewOrderModal's giftBoxNeeded) — a
  // customer buying 7 sets gets 7 boxes, not 1.
  const comboSetsQty = cart.filter((item) => isGiftBoxSet(item.slug)).reduce((sum, item) => sum + item.qty, 0);
  // Gift Box can be turned off entirely from ERP (Inventory > Products >
  // GIFT-BOX > Status = Inactive) — giftBoxPrice comes back null in that
  // case, so nothing here should offer or silently include it.
  const giftBoxAvailable = giftBoxPrice != null;
  // Free with the combo (qty follows the combo's own set count, not a
  // choice), otherwise an opt-in add-on the customer can pick any quantity
  // of — defaults to 0 so an extra charge is never silently pre-selected.
  const [giftBoxQty, setGiftBoxQty] = useState(0);
  const giftBoxNeeded = hasCombo ? comboSetsQty : giftBoxAvailable ? giftBoxQty : 0;
  const giftBoxCharge = !hasCombo ? giftBoxNeeded * (giftBoxPrice ?? 0) : 0;

  const total = totalPrice - discountAmount + finalShipping + giftBoxCharge;

  // Cash on Delivery is always available; every other option is synced live
  // from ERP > Settings > Payment Gateways and only shown here once enabled
  // there — a disabled gateway doesn't appear on checkout at all.
  const paymentOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [{ value: 'cod', label: t('checkout.cod', 'Cash on Delivery') }];
    for (const g of paymentGateways) {
      if (!g.is_enabled) continue;
      options.push({ value: g.gateway_key, label: g.display_name });
    }
    return options;
  }, [paymentGateways, t]);

  const handleChange = (field: keyof CheckoutForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return; // 중복 클릭/재제출 방지
    if (!isRequiredFieldsFilled || !agreedToTerms) return;

    // Only trust a non-cod selection if it's currently an enabled gateway —
    // the server re-checks this too, but falling back here avoids sending a
    // stale/disabled value if paymentGateways hasn't loaded yet for
    // whatever reason.
    const selectedGateway = paymentGateways.find((g) => g.gateway_key === form.paymentMethod);
    const paymentMethod = form.paymentMethod === 'cod' || selectedGateway?.is_enabled ? form.paymentMethod : 'cod';

    // 카트 slug → 컨트롤 코어 SKU 매핑 (미매핑 발견 시 접수 차단)
    const items: { sku: string; qty: number }[] = [];
    for (const item of cart) {
      const sku = slugToSku(item.slug);
      if (!sku) {
        setErrorCode('validation');
        return;
      }
      items.push({ sku, qty: item.qty });
    }
    // Price is never sent — the server decides free-with-combo vs. the
    // product's own MSRP (see core_create_order).
    if (giftBoxNeeded > 0) {
      items.push({ sku: 'GIFT-BOX', qty: giftBoxNeeded });
    }

    // 배송 주소를 한 줄로 합성(컨트롤 코어 customer.address는 단일 텍스트)
    const address = [
      form.address1,
      form.address2,
      form.area,
      form.district,
      form.postalCode,
    ]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(', ');

    setErrorCode(null);
    setLoading(true);

    if (paymentMethod !== 'cod') {
      // 온라인 결제 게이트웨이 — 결제가 확인되기 전까지는 주문을 만들지
      // 않는다(core-init-gateway-payment는 세션만 열 뿐 core_orders를
      // 건드리지 않음). 여기서는 장바구니를 비우지 않고 그대로 두어,
      // 결제가 실패/취소돼 돌아와도 손님이 다시 담을 필요가 없게 한다.
      const gwResult = await initGatewayPayment({
        customer: {
          name: form.fullName.trim(),
          phone: form.phone.trim(),
          address,
          note: form.deliveryNote,
          email: form.email.trim() || undefined,
          city: form.city,
        },
        items,
        paymentMethod,
        customerAccessToken: isAuthenticated ? session?.access_token : undefined,
        couponCode: couponDiscount?.discount.code ?? undefined,
      });

      if (!gwResult.ok) {
        // tsconfig has `strict: false`, which disables discriminated-union
        // narrowing on boolean-literal fields like `ok` — the explicit cast
        // is needed for the compiler even though `.ok === false` guarantees
        // this is the failure branch at runtime.
        setErrorCode((gwResult as InitGatewayPaymentFailure).code);
        setLoading(false);
        return;
      }

      if (isAuthenticated && currentUser) {
        saveShippingAddress(currentUser.id, {
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          area: form.area,
          postalCode: form.postalCode,
        }).catch(() => {});
      }

      // 결제 결과 페이지(/checkout/payment/:tranId)가 영수증 사본을 만들 때
      // 쓸 수 있도록 최소 정보만 임시 보관 — 금액은 서버가 결제 확정 시점에
      // 다시 계산해 알려주므로 여기서 저장하지 않는다.
      savePendingCheckoutSnapshot(gwResult.tranId, {
        items: cart.map((item) => ({ slug: item.slug, qty: item.qty })),
        contact: { fullName: form.fullName, phone: form.phone, email: form.email },
        shipping: {
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          area: form.area,
          postalCode: form.postalCode,
          deliveryNote: form.deliveryNote,
        },
        paymentMethod,
      });

      window.location.href = gwResult.gatewayUrl;
      return;
    }

    // 정본은 서버 — 컨트롤 코어(core-place-order)에 주문 접수
    const result = await placeCoreOrder({
      customer: {
        name: form.fullName.trim(),
        phone: form.phone.trim(),
        address,
        note: form.deliveryNote,
        email: form.email.trim() || undefined,
        city: form.city,
      },
      items,
      paymentMethod,
      customerAccessToken: isAuthenticated ? session?.access_token : undefined,
      couponCode: couponDiscount?.discount.code ?? undefined,
    });

    if (!result.ok) {
      // This number has a history of cancelled COD orders — steer it onto
      // an online gateway instead of leaving 'cod' selected, so the retry
      // the error message invites actually has somewhere to go.
      if (result.code === 'cod_blocked') {
        const firstEnabled = paymentGateways.find((g) => g.is_enabled);
        if (firstEnabled) setForm((prev) => ({ ...prev, paymentMethod: firstEnabled.gateway_key }));
      }
      setErrorCode(result.code);
      setLoading(false);
      return;
    }

    // Remember this shipping address on the customer's profile so future
    // checkouts prefill it (see useEffect above). Non-blocking — a failure
    // here shouldn't undo an order that already succeeded.
    if (isAuthenticated && currentUser) {
      saveShippingAddress(currentUser.id, {
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        area: form.area,
        postalCode: form.postalCode,
      }).catch(() => {});
    }

    // 서버가 발급한 order_no가 정본 식별자(고객 안내용). 없으면 order_id로 대체.
    const orderRef = result.orderNo ?? result.orderId;

    // localStorage에는 "영수증 사본"으로만 저장(정본은 서버 core_orders).
    saveReceiptCopy({
      id: orderRef,
      orderNo: result.orderNo,
      orderId: result.orderId,
      items: cart.map((item) => ({ ...item })),
      contact: { fullName: form.fullName, phone: form.phone, email: form.email },
      shipping: {
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        area: form.area,
        postalCode: form.postalCode,
        deliveryNote: form.deliveryNote,
      },
      payment: { method: paymentMethod },
      subtotal: totalPrice,
      discount: result.discountAmountBdt,
      shippingCost: finalShipping,
      total: totalPrice - result.discountAmountBdt + finalShipping + giftBoxCharge,
      createdAt: new Date().toISOString(),
    });

    trackPurchase(
      orderRef,
      cart.map((item) => {
        const info = getProductInfo(item.slug);
        return { id: item.slug, name: info?.name ?? item.slug, price: info ? parsePrice(info.price) : 0, quantity: item.qty };
      }),
      totalPrice - result.discountAmountBdt + finalShipping + giftBoxCharge,
    );

    clearCart();
    setLoading(false);
    navigate(`${prefix}/order/${encodeURIComponent(orderRef)}`);
  };

  if (cart.length === 0) {
    return (
      <>
        <SharedNavbar />
        <div className="w-full min-h-screen bg-white flex items-center justify-center pt-24 pb-20">
          <div className="text-center flex flex-col gap-4">
            <div className="w-16 h-16 rounded-full bg-[#F0EFED] flex items-center justify-center mx-auto">
              <i className="ri-shopping-bag-line text-2xl text-[#8A8A8A]"></i>
            </div>
            <h1 className="font-serif text-2xl font-bold text-[#2A2A2A]">{t('cart.empty', 'Your cart is empty')}</h1>
            <button
              onClick={() => navigate(prefix + '/products')}
              className="px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('common.continueShopping')}
            </button>
          </div>
        </div>
        <SharedFooter />
      </>
    );
  }

  return (
    <>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto px-4 md:px-12 lg:px-20 py-8 md:py-10">
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-6 md:mb-8">{t('checkout.title', 'Checkout')}</h1>
          {/* Guests check out straight away; signing in is an optional link, not a blocking popup. */}
          {!isAuthenticated && (
            <p className="-mt-3 md:-mt-5 mb-6 text-[13px] text-[#8A8A8A]">
              {t('checkout.guestModal.subtitle', 'Sign in for a faster experience and mileage rewards')}{' '}
              <Link to={prefix + '/login'} className="text-[#7B1113] font-medium underline underline-offset-2">
                {t('auth.login.title')}
              </Link>
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Left: Form */}
            <form onSubmit={handleSubmit} className="order-1 flex flex-col gap-4 md:gap-5">
              {/* Contact & Shipping */}
              <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#E8E8E6]">
                <h2 className="flex items-center gap-2 text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-3">
                  <i className="ri-map-pin-line text-base text-[#7B1113]"></i>
                  {t('checkout.contactShipping', 'Contact & Shipping')}
                </h2>
                <div className="flex flex-col gap-2.5">
                  <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                    <input
                      type="text"
                      required
                      autoComplete="name"
                      autoCapitalize="words"
                      inputMode="text"
                      placeholder={t('checkout.fullName', 'Full Name')}
                      value={form.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      className="w-full px-3.5 py-3 md:py-2.5 rounded-xl border border-[#E8E8E6] text-base md:text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors"
                    />
                    <input
                      type="tel"
                      required
                      autoComplete="tel"
                      inputMode="tel"
                      placeholder={t('checkout.phone', 'Phone')}
                      value={form.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="w-full px-3.5 py-3 md:py-2.5 rounded-xl border border-[#E8E8E6] text-base md:text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors"
                    />
                  </div>
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder={t('checkout.email', 'Email (Optional)')}
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-3.5 py-3 md:py-2.5 rounded-xl border border-[#E8E8E6] text-base md:text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors"
                  />

                  <div className="h-px bg-[#E8E8E6] my-1" />

                  <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                    <SearchableSelect
                      value={form.district}
                      onChange={(district) => {
                        // Changing district invalidates whatever thana was
                        // picked before (it belonged to the old district's
                        // list) — reset it so a stale, no-longer-valid
                        // selection can't silently stay in the form.
                        setForm((prev) => ({ ...prev, district, area: '', city: district === 'Dhaka' ? 'Dhaka' : 'Outside Dhaka' }));
                      }}
                      options={ALL_DISTRICT_NAMES}
                      placeholder={t('checkout.selectDistrict', 'Select District')}
                      searchPlaceholder={t('checkout.searchDistrict', 'Search district…')}
                    />
                    <SearchableSelect
                      value={form.area}
                      onChange={(thana) => handleChange('area', thana)}
                      options={selectedDistrictUpazilas}
                      placeholder={t('checkout.selectThana', 'Select Thana')}
                      searchPlaceholder={t('checkout.searchThana', 'Search thana…')}
                      disabled={!form.district}
                    />
                  </div>
                  <input
                    type="text"
                    required
                    autoComplete="address-line1"
                    autoCapitalize="words"
                    inputMode="text"
                    placeholder={t('checkout.address1', 'Full Address')}
                    value={form.address1}
                    onChange={(e) => handleChange('address1', e.target.value)}
                    className="w-full px-3.5 py-3 md:py-2.5 rounded-xl border border-[#E8E8E6] text-base md:text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors"
                  />
                  {/* Delivery note — collapsed by default since most orders
                      don't need one; matches the coupon box's expand pattern
                      below so an optional field doesn't cost permanent space. */}
                  {noteBoxOpen ? (
                    <textarea
                      autoFocus
                      placeholder={t('checkout.deliveryNotePlaceholder', 'Any special instructions for delivery (optional)')}
                      value={form.deliveryNote}
                      onChange={(e) => handleChange('deliveryNote', e.target.value)}
                      maxLength={200}
                      rows={2}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[#E8E8E6] text-base md:text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors resize-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setNoteBoxOpen(true)}
                      className="flex items-center gap-1.5 text-xs text-[#8A8A8A] hover:text-[#2A2A2A] transition-colors cursor-pointer w-fit"
                    >
                      <i className="ri-add-line text-sm"></i>
                      <span className="font-light">{t('checkout.addDeliveryNote', 'Add a delivery note')}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#E8E8E6]">
                <h2 className="flex items-center gap-2 text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-3">
                  <i className="ri-bank-card-line text-base text-[#7B1113]"></i>
                  {t('checkout.payment', 'Payment Method')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {paymentOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border cursor-pointer transition-colors min-h-[46px] ${
                        form.paymentMethod === opt.value ? 'border-[#7B1113] bg-[#7B1113]/5' : 'border-[#E8E8E6] hover:border-[#8A8A8A]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={opt.value}
                        checked={form.paymentMethod === opt.value}
                        onChange={() => handleChange('paymentMethod', opt.value)}
                        className="accent-[#7B1113] w-5 h-5 md:w-4 md:h-4 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-sm font-medium text-[#2A2A2A]">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </form>

            {/* Right: Order Summary — comes after the form (order-2) on
                mobile so the actual task (filling it in) isn't blocked by a
                tall summary card first; stays on the right on desktop where
                both are visible at once. */}
            <div className="order-2 lg:sticky lg:top-24 h-fit">
              <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#E8E8E6]">
                <h2 className="flex items-center gap-2 text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-4 md:mb-5">
                  <i className="ri-shopping-bag-3-line text-base text-[#7B1113]"></i>
                  {t('checkout.orderSummary', 'Order Summary')}
                </h2>

                <div className="flex flex-col gap-2.5 md:gap-4 mb-4 md:mb-6">
                  {cart.map((item) => {
                    const p = getProductInfo(item.slug);
                    if (!p) return null;
                    const unit = parsePrice(p.price);
                    const isSet = item.slug === '3-step-set';
                    const originalUnit = isSet && routineSet ? parsePrice(routineSet.originalPrice) : null;
                    return (
                      <div key={item.slug} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-[#F0EFED] flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#7B1113]">{item.qty}x</span>
                        </div>
                        <div className="flex-1 flex flex-col min-w-0">
                          <span className="text-sm text-[#2A2A2A] truncate">{p.name}</span>
                        </div>
                        <div className="flex flex-col items-end flex-shrink-0">
                          {originalUnit != null && (
                            <span className="text-[11px] text-[#8A8A8A] line-through">৳{(originalUnit * item.qty).toLocaleString()}</span>
                          )}
                          <span className="text-sm font-medium text-[#2A2A2A]">৳{(unit * item.qty).toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Coupon code */}
                <div className="border-t border-[#E8E8E6] pt-4 md:pt-5 mb-4 md:mb-5">
                  {couponDiscount ? (
                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-[#7B1113]/5">
                      <span className="text-xs text-[#7B1113] font-medium">
                        {t('checkout.couponApplied', 'Coupon applied')}: {couponDiscount.discount.code}
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        className="text-xs text-[#8A8A8A] hover:text-[#7B1113] underline cursor-pointer flex-shrink-0"
                      >
                        {t('checkout.remove', 'Remove')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {autoDiscount && (
                        <p className="text-[11px] text-[#7B1113] font-medium">
                          🎉 {autoDiscount.discount.label} — {t('checkout.couponApplied', 'Coupon applied')}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => setCouponBoxOpen((v) => !v)}
                        className="flex items-center justify-between gap-2 text-xs text-[#8A8A8A] hover:text-[#2A2A2A] transition-colors cursor-pointer"
                      >
                        <span className="font-light">{t('checkout.haveCoupon', 'Have a coupon code?')}</span>
                        <i className={`ri-arrow-down-s-line text-sm transition-transform ${couponBoxOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      {couponBoxOpen && (
                        <div className="flex flex-col gap-1.5 mt-1">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              autoFocus
                              value={couponInput}
                              onChange={(e) => setCouponInput(e.target.value)}
                              placeholder={t('checkout.couponPlaceholder', 'Enter code')}
                              className="flex-1 px-3 py-2 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors uppercase"
                            />
                            <button
                              type="button"
                              onClick={handleApplyCoupon}
                              disabled={checkingCoupon || !couponInput.trim()}
                              className="px-5 py-2 rounded-xl border border-[#2A2A2A]/20 text-xs font-medium text-[#2A2A2A] hover:border-[#7B1113] hover:text-[#7B1113] transition-colors disabled:opacity-50 cursor-pointer"
                            >
                              {t('checkout.apply', 'Apply')}
                            </button>
                          </div>
                          {couponError && <p className="text-[11px] text-[#7B1113]">{couponError}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t border-[#E8E8E6] pt-4 md:pt-5 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#8A8A8A] font-light">{t('cart.subtotal', 'Subtotal')}</span>
                    <span className="text-sm text-[#2A2A2A]">৳{displaySubtotal.toLocaleString()}</span>
                  </div>
                  {comboDiscount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#7B1113] font-light">{t('checkout.comboDiscount', 'Combo Discount')}</span>
                      <span className="text-sm text-[#7B1113] font-medium">-৳{comboDiscount.toLocaleString()}</span>
                    </div>
                  )}
                  {effectiveDiscount && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#7B1113] font-light">{t('checkout.discount', 'Discount')}</span>
                      <span className="text-sm text-[#7B1113] font-medium">-৳{discountAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#8A8A8A] font-light">{t('cart.shipping', 'Shipping')}</span>
                    <span className="text-sm text-[#2A2A2A]">
                      {freeShipping ? (
                        <span className="text-[#7B1113] font-medium">{t('cart.free', 'Free')}</span>
                      ) : (
                        `৳${finalShipping}`
                      )}
                    </span>
                  </div>
                  {freeShipping && (
                    <p className="text-[10px] text-[#8A8A8A] font-light">
                      {t('checkout.freeShippingNote', `Free shipping on orders over ৳${shippingRates.freeThreshold.toLocaleString()}`, {
                        threshold: shippingRates.freeThreshold.toLocaleString(),
                      })}
                    </p>
                  )}
                  {giftBoxAvailable && (
                    <>
                      {hasCombo ? (
                        // Combo orders always get the gift box — not a
                        // choice, so no checkbox to turn it off.
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#8A8A8A] font-light">🎁 {t('checkout.giftBoxIncluded', 'Gift Box Included')}</span>
                          <span className="text-sm text-[#7B1113] font-medium">{t('cart.free', 'Free')}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#8A8A8A] font-light">🎁 {t('checkout.giftBox', 'Add a Gift Box')}</span>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center border border-[#E8E8E6] rounded-full">
                              <button
                                type="button"
                                onClick={() => setGiftBoxQty((q) => Math.max(0, q - 1))}
                                disabled={giftBoxQty === 0}
                                className="w-6 h-6 flex items-center justify-center text-[#8A8A8A] disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
                                aria-label="Decrease gift box quantity"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-5 text-center text-sm text-[#2A2A2A]">{giftBoxQty}</span>
                              <button
                                type="button"
                                onClick={() => setGiftBoxQty((q) => q + 1)}
                                className="w-6 h-6 flex items-center justify-center text-[#8A8A8A] cursor-pointer"
                                aria-label="Increase gift box quantity"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            {giftBoxQty > 0 && <span className="text-sm text-[#2A2A2A]">+৳{giftBoxCharge.toLocaleString()}</span>}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-[#E8E8E6]">
                    <span className="text-base font-medium text-[#2A2A2A]">{t('cart.total', 'Total')}</span>
                    <span className="text-xl font-serif font-bold text-[#2A2A2A]">৳{total.toLocaleString()}</span>
                  </div>
                </div>

                {errorCode && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mt-5 md:mt-6 flex items-start gap-2 rounded-xl border border-[#7B1113]/20 bg-[#7B1113]/5 px-4 py-3 text-sm text-[#7B1113]"
                  >
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                    <span>
                      {t(
                        `checkout.error.${
                          errorCode === 'insufficient_stock'
                            ? 'insufficientStock'
                            : errorCode === 'cod_blocked'
                              ? 'codBlocked'
                              : errorCode === 'network'
                                ? 'network'
                                : errorCode === 'config'
                                  ? 'config'
                                  : errorCode === 'validation'
                                    ? 'validation'
                                    : 'server'
                        }`,
                      )}
                    </span>
                  </div>
                )}

                <label className={`flex items-start gap-2.5 ${errorCode ? 'mt-3' : 'mt-5 md:mt-6'} cursor-pointer select-none`}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 accent-[#7B1113] w-4 h-4 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs text-[#8A8A8A] leading-relaxed">
                    {t('checkout.agreePrefix', 'I have read and agree to the')}{' '}
                    <Link to={prefix + '/terms-of-service'} target="_blank" className="text-[#7B1113] hover:underline">
                      {t('checkout.termsLink', 'Terms & Conditions')}
                    </Link>
                    ,{' '}
                    <Link to={prefix + '/privacy-policy'} target="_blank" className="text-[#7B1113] hover:underline">
                      {t('checkout.privacyLink', 'Privacy Policy')}
                    </Link>{' '}
                    {t('checkout.agreeAnd', 'and')}{' '}
                    <Link to={prefix + '/shipping-returns'} target="_blank" className="text-[#7B1113] hover:underline">
                      {t('checkout.returnsLink', 'Return & Refund Policy')}
                    </Link>
                    .
                  </span>
                </label>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !isRequiredFieldsFilled || !agreedToTerms}
                  className={`w-full mt-3 py-4 md:py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-center min-h-[52px]`}
                >
                  {loading ? t('checkout.placing', 'Placing your order...') : `${t('common.placeOrder')} →`}
                </button>

                <div className="flex items-center justify-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5 text-[10px] text-[#8A8A8A]">
                    <i className="ri-shield-check-line text-[#7B1113]"></i>
                    <span>{t('checkout.authenticityGuaranteed', 'Authenticity Guaranteed')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-[#8A8A8A]">
                    <i className="ri-lock-line text-[#7B1113]"></i>
                    <span>{t('checkout.secureCheckout', 'Secure Checkout')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SharedFooter />
    </>
  );
}